import type { DisplayLocale } from "@openrift/shared/types/api/preferences";
import { DISPLAY_LOCALES } from "@openrift/shared/types/api/preferences";
import { useQueryClient } from "@tanstack/react-query";
import type { LinkProps } from "@tanstack/react-router";
import { Link, useLocation, useMatch, useRouter } from "@tanstack/react-router";
import {
  CircleHelpIcon,
  ExternalLinkIcon,
  EllipsisVerticalIcon,
  HeartIcon,
  LanguagesIcon,
  LockIcon,
  LogOutIcon,
  MenuIcon,
  MoonIcon,
  PencilLineIcon,
  SearchIcon,
  ShieldIcon,
  SmartphoneIcon,
  SparklesIcon,
  SunIcon,
  UserIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { siDiscord, siGithub } from "simple-icons";
import latestMilestone from "virtual:latest-milestone";

import type { NavFlags } from "@/components/layout/nav-items";
import {
  badgeAriaLabel,
  keepsSearch,
  SignInRequiredDialog,
  visibleMoreSections,
  visiblePrimaryItems,
} from "@/components/layout/nav-items";
import { Badge } from "@/components/ui/badge";
import { BrandGlyph } from "@/components/ui/brand-glyph";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ExpandToggle } from "@/components/ui/expand-toggle";
import { Kbd } from "@/components/ui/kbd";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLabel,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { UserAvatar } from "@/components/user-avatar";
import { applyDisplayLocale } from "@/features/account/hooks/use-preferences-sync";
import { useAdminAccess } from "@/features/admin/hooks/use-admin";
import { useAddModeStore } from "@/features/collections/stores/add-mode-store";
import { usePaletteStore } from "@/features/collections/stores/palette-store";
import { useDeckBuilderUiStore } from "@/features/decks/stores/deck-builder-ui-store";
import { useBadges } from "@/features/groups/hooks/use-badges";
import { milestoneBannerDecision } from "@/features/marketing/lib/milestone-banner";
import { useFeatureEnabled } from "@/hooks/use-feature-flags";
import { useGravatarHash } from "@/hooks/use-gravatar-hash";
import { useHydrated } from "@/hooks/use-hydrated";
import { useSession } from "@/hooks/use-session";
import { sessionQueryOptions } from "@/lib/auth-session";
import { DISPLAY_LOCALE_LABELS } from "@/lib/display-locale";
import { isStandaloneDisplay } from "@/lib/install-platform";
import type { LockedFeatureKey, NavBadgeCounts, NavItemConfig } from "@/lib/nav-items";
import { SOCIAL_LINKS } from "@/lib/social-links";
import { STICKY_SURFACE } from "@/lib/sticky-surface";
import { toastError } from "@/lib/toast";
import { cn, CONTAINER_WIDTH } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";
import { useCommandPaletteStore } from "@/stores/command-palette-store";
import { useDisplayStore } from "@/stores/display-store";
import { useMilestoneBannerStore } from "@/stores/milestone-banner-store";
import { useThemeStore } from "@/stores/theme-store";

function LogoLink({ className }: { className?: string }) {
  const isHome = useMatch({ from: "/_app/cards", shouldThrow: false });

  return (
    <Link
      to="/cards"
      className={cn("flex items-center gap-2", className)}
      onClick={(e) => {
        if (isHome) {
          e.preventDefault();
          globalThis.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    >
      <img src="/logo-color.svg" alt="OpenRift" className="size-8" />
      <span className="font-heading text-xl font-bold">OpenRift</span>
    </Link>
  );
}

function MenuButton({ onClick, className }: { onClick: () => void; className?: string }) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label={m.layout_header_open_menu()}
      className={className}
      onClick={onClick}
    >
      <MenuIcon className="size-5" />
    </Button>
  );
}

// Pill background is hover/focus-only, active is text-only, or an active item
// next to a hovered one renders as two same-color pills fused at their corners.
const DESKTOP_NAV_ITEM_CLASS = cn(
  navigationMenuTriggerStyle(),
  "text-muted-foreground hover:text-foreground focus:text-foreground data-[status=active]:text-foreground data-[status=active]:font-semibold",
);

function DesktopPrimaryItem({
  item,
  isLoggedIn,
  badges,
  onLockedClick,
}: {
  item: NavItemConfig;
  isLoggedIn: boolean;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  const lockedKey = item.lockedKey;
  const keepSearch = useLocation({ select: (location) => keepsSearch(item, location.pathname) });
  if (lockedKey && !isLoggedIn) {
    return (
      <NavigationMenuLink
        // oxlint-disable-next-line jsx-a11y/control-has-associated-label, react/forbid-elements -- bare render slot; NavigationMenuLink owns all styling and provides the label as children
        render={<button type="button" onClick={() => onLockedClick(lockedKey)} />}
        className={cn(DESKTOP_NAV_ITEM_CLASS, "gap-1.5")}
      >
        {item.label}
        <LockIcon className="text-muted-foreground size-3.5" />
      </NavigationMenuLink>
    );
  }
  const badgeCount = item.badge ? badges[item.badge] : 0;
  return (
    <NavigationMenuLink
      render={<Link to={item.to} search={keepSearch ? (prev) => prev : undefined} />}
      className={DESKTOP_NAV_ITEM_CLASS}
    >
      {item.label}
      {item.badge && badgeCount > 0 && (
        <Badge
          variant="count"
          aria-label={badgeAriaLabel(item.badge, badgeCount)}
          className="ml-1.5"
        >
          {badgeCount > 9 ? "9+" : badgeCount}
        </Badge>
      )}
    </NavigationMenuLink>
  );
}

function DesktopMoreItem({
  item,
  isLoggedIn,
  badges,
  onLockedClick,
}: {
  item: NavItemConfig;
  isLoggedIn: boolean;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  const lockedKey = item.lockedKey;
  const badgeCount = item.badge ? badges[item.badge] : 0;
  const content = (
    <>
      <item.icon />
      <div>
        <div className="font-medium">
          {item.label}
          {item.badge && badgeCount > 0 && (
            <Badge
              variant="count"
              aria-label={badgeAriaLabel(item.badge, badgeCount)}
              className="ml-1.5"
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </Badge>
          )}
        </div>
        <div className="text-muted-foreground text-xs">{item.description}</div>
      </div>
    </>
  );
  if (lockedKey && !isLoggedIn) {
    return (
      <NavigationMenuLink
        closeOnClick
        // A native <button> shrinks to its content and centers its text; force
        // it to fill and left-align so it matches the <Link>-rendered rows.
        className="w-full text-left"
        // oxlint-disable-next-line jsx-a11y/control-has-associated-label, react/forbid-elements -- bare render slot; NavigationMenuLink owns all styling and provides the label as children
        render={<button type="button" onClick={() => onLockedClick(lockedKey)} />}
      >
        {content}
        <LockIcon className="text-muted-foreground ml-auto size-3.5 self-center" />
      </NavigationMenuLink>
    );
  }
  return (
    <NavigationMenuLink closeOnClick render={<Link to={item.to} />}>
      {content}
    </NavigationMenuLink>
  );
}

function DesktopNav({
  isLoggedIn,
  flags,
  badges,
  onLockedClick,
}: {
  isLoggedIn: boolean;
  flags: NavFlags;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  return (
    <NavigationMenu>
      <NavigationMenuList className="gap-1">
        {visiblePrimaryItems({ flags, mobile: false, badges }).map((item) => (
          <NavigationMenuItem key={item.to}>
            <DesktopPrimaryItem
              item={item}
              isLoggedIn={isLoggedIn}
              badges={badges}
              onLockedClick={onLockedClick}
            />
          </NavigationMenuItem>
        ))}
        <NavigationMenuItem>
          <NavigationMenuTrigger className="text-muted-foreground hover:text-foreground focus:text-foreground data-popup-open:text-foreground">
            {m.layout_header_more()}
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            {/* CSS columns pack the sections side by side; break-inside-avoid
                keeps each section whole so a group never splits mid-column. */}
            <div className="w-[34rem] columns-2 gap-2 p-2">
              {visibleMoreSections({ flags, mobile: false, badges }).map((section) => (
                <section key={section.label} className="mb-3 break-inside-avoid last:mb-0">
                  <NavigationMenuLabel>{section.label}</NavigationMenuLabel>
                  <ul className="grid gap-1">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <DesktopMoreItem
                          item={item}
                          isLoggedIn={isLoggedIn}
                          badges={badges}
                          onLockedClick={onLockedClick}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}

function UserMenuTrigger({
  user,
}: {
  user: { name: string; email: string; image?: string | null } | undefined;
}) {
  const gravatarHash = useGravatarHash(user?.email);
  if (user) {
    return (
      <UserAvatar
        image={user.image}
        name={user.name}
        email={user.email}
        gravatarHash={gravatarHash}
        size="sm"
      />
    );
  }
  return <EllipsisVerticalIcon className="size-5" />;
}

// Loaded on click so the auth client stays out of the shell's entry chunk.
async function signOutViaAuthClient(): Promise<void> {
  const { signOut } = await import("@/features/account/lib/auth-client");
  await signOut();
}

function LanguageSubmenu({ isLoggedIn }: { isLoggedIn: boolean }) {
  const active = getLocale();

  const change = async (next: DisplayLocale) => {
    if (!(await applyDisplayLocale(next, { persist: isLoggedIn }))) {
      toastError(m.profile_display_locale_error());
    }
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <LanguagesIcon className="size-4" />
        {m.locale_switcher_label()}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup value={active} onValueChange={(next) => void change(next)}>
          {DISPLAY_LOCALES.map((locale) => (
            <DropdownMenuRadioItem key={locale} value={locale} lang={locale}>
              {DISPLAY_LOCALE_LABELS[locale]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function UserMenuItems({ isLoggedIn }: { isLoggedIn: boolean }) {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const darkMode = theme === "dark";
  const { data: adminAccess } = useAdminAccess();
  const hasAdminAccess =
    adminAccess !== undefined && (adminAccess.isAdmin || adminAccess.sections.length > 0);
  const queryClient = useQueryClient();
  const router = useRouter();

  const handleSignOut = async () => {
    try {
      await signOutViaAuthClient();
      useDisplayStore.getState().reset();
      useThemeStore.getState().reset();
      usePaletteStore.getState().reset();
      useAddModeStore.getState().reset();
      useDeckBuilderUiStore.getState().reset();
      // Navigate before invalidating the session query: a still-mounted
      // authenticated component would see the null session and crash on useRequiredUserId.
      await router.navigate({ to: "/cards", search: {} });
      void queryClient.invalidateQueries({ queryKey: sessionQueryOptions().queryKey });
    } catch {
      toastError(m.layout_header_sign_out_failed());
    }
  };

  return (
    <DropdownMenuContent align="end">
      {isLoggedIn && (
        <DropdownMenuItem render={<Link to="/profile" />}>
          <UserIcon className="size-4" />
          {m.layout_header_profile()}
        </DropdownMenuItem>
      )}
      {isLoggedIn && hasAdminAccess && (
        <DropdownMenuItem render={<Link to="/admin" />}>
          <ShieldIcon className="size-4" />
          {m.layout_header_admin()}
        </DropdownMenuItem>
      )}
      {isLoggedIn && <DropdownMenuSeparator />}
      {!isLoggedIn && (
        <DropdownMenuItem onClick={toggleTheme}>
          {darkMode ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
          {darkMode ? m.layout_header_light_mode() : m.layout_header_dark_mode()}
        </DropdownMenuItem>
      )}
      <LanguageSubmenu isLoggedIn={isLoggedIn} />
      {isLoggedIn && <DropdownMenuSeparator />}
      {isLoggedIn && (
        <DropdownMenuItem onClick={() => void handleSignOut()}>
          <LogOutIcon className="size-4" />
          {m.layout_header_sign_out()}
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  );
}

// Signing in from one of these bounces through an auth page or the marketing
// page, so they carry no redirect and fall through to the post-sign-in default.
const NO_SIGN_IN_REDIRECT: ReadonlySet<string> = new Set([
  "/",
  "/login",
  "/signup",
  "/reset-password",
  "/verify-email",
]);

/** Picks the `redirect` the header's Sign in button carries from the current location. */
export function signInRedirectFor(location: {
  pathname: string;
  href: string;
}): string | undefined {
  return NO_SIGN_IN_REDIRECT.has(location.pathname) ? undefined : location.href;
}

function UserMenu({
  session,
  isPending,
}: {
  session: ReturnType<typeof useSession>["data"];
  isPending: boolean;
}) {
  const isLoggedIn = Boolean(session?.user);
  const signInRedirect = useLocation({ select: signInRedirectFor });

  if (isPending) {
    return <div className="size-8" />;
  }

  const user = session?.user;

  return (
    <div className="flex items-center gap-2">
      {!user && (
        <Link
          rel="nofollow"
          to="/login"
          search={{ redirect: signInRedirect, email: undefined }}
          className={buttonVariants({ size: "sm" })}
        >
          {m.common_sign_in()}
        </Link>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon-sm" aria-label={m.layout_header_menu()} />}
        >
          <UserMenuTrigger user={user} />
        </DropdownMenuTrigger>
        <UserMenuItems isLoggedIn={isLoggedIn} />
      </DropdownMenu>
    </div>
  );
}

// Same active-state treatment as DESKTOP_NAV_ITEM_CLASS: background for
// hover only, text emphasis for the active route.
const MOBILE_NAV_ITEM_CLASS =
  "text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-3 rounded-lg px-3 py-3.5 text-base data-[status=active]:font-semibold data-[status=active]:text-foreground";

function MobileNavItem({
  item,
  compact,
  isLoggedIn,
  badges,
  onLockedClick,
}: {
  item: NavItemConfig;
  compact?: boolean;
  isLoggedIn: boolean;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  const lockedKey = item.lockedKey;
  const keepSearch = useLocation({ select: (location) => keepsSearch(item, location.pathname) });
  const rowClass = cn(MOBILE_NAV_ITEM_CLASS, compact && "py-2.5");
  const icon = <item.icon className="text-muted-foreground size-5" />;
  if (lockedKey && !isLoggedIn) {
    return (
      <SheetClose
        // oxlint-disable-next-line jsx-a11y/control-has-associated-label, react/forbid-elements -- bare render slot; SheetClose/MOBILE_NAV_ITEM_CLASS owns all styling and provides the label as children
        render={<button type="button" onClick={() => onLockedClick(lockedKey)} />}
        className={rowClass}
      >
        {icon}
        {item.label}
        <LockIcon className="text-muted-foreground ml-auto size-4" />
      </SheetClose>
    );
  }
  const badgeCount = item.badge ? badges[item.badge] : 0;
  return (
    <SheetClose
      nativeButton={false}
      render={<Link to={item.to} search={keepSearch ? (prev) => prev : undefined} />}
      className={rowClass}
    >
      {icon}
      {item.label}
      {item.badge && badgeCount > 0 && (
        <Badge
          variant="count"
          aria-label={badgeAriaLabel(item.badge, badgeCount)}
          className="ml-auto"
        >
          {badgeCount > 9 ? "9+" : badgeCount}
        </Badge>
      )}
    </SheetClose>
  );
}

function MobileNavSection({
  section,
  isLoggedIn,
  badges,
  onLockedClick,
}: {
  section: { label: string; items: NavItemConfig[] };
  isLoggedIn: boolean;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hiddenBadgeCount = expanded
    ? 0
    : section.items.reduce((sum, item) => sum + (item.badge ? badges[item.badge] : 0), 0);

  return (
    <div className="mt-2 flex flex-col gap-1">
      <ExpandToggle
        expanded={expanded}
        chevronPosition="end"
        chevronClassName="size-3.5"
        onClick={() => setExpanded((prev) => !prev)}
        className="text-muted-foreground hover:text-foreground w-full justify-between rounded-lg px-3 py-2 text-xs font-medium tracking-wide uppercase"
      >
        <span className="flex items-center gap-2">
          {section.label}
          {hiddenBadgeCount > 0 && (
            <Badge
              variant="count"
              aria-label={m.layout_header_section_attention({ section: section.label })}
            >
              {hiddenBadgeCount > 9 ? "9+" : hiddenBadgeCount}
            </Badge>
          )}
        </span>
      </ExpandToggle>
      {expanded &&
        section.items.map((item) => (
          <MobileNavItem
            key={item.to}
            item={item}
            compact
            isLoggedIn={isLoggedIn}
            badges={badges}
            onLockedClick={onLockedClick}
          />
        ))}
    </div>
  );
}

function MobileNav({
  open,
  onOpenChange,
  isLoggedIn,
  flags,
  badges,
  onLockedClick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoggedIn: boolean;
  flags: NavFlags;
  badges: NavBadgeCounts;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  const hydrated = useHydrated();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left">
        <SheetHeader>
          <SheetTitle>
            <Link
              to="/cards"
              className="font-heading flex items-center gap-2 font-bold"
              onClick={() => onOpenChange(false)}
            >
              <img src="/logo-color.svg" alt="OpenRift" className="size-6" />
              OpenRift
            </Link>
          </SheetTitle>
        </SheetHeader>
        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-2">
          {visiblePrimaryItems({ flags, mobile: true, badges }).map((item) => (
            <MobileNavItem
              key={item.to}
              item={item}
              isLoggedIn={isLoggedIn}
              badges={badges}
              onLockedClick={onLockedClick}
            />
          ))}
          {visibleMoreSections({ flags, mobile: true, badges }).map((section) => (
            <MobileNavSection
              key={section.label}
              section={section}
              isLoggedIn={isLoggedIn}
              badges={badges}
              onLockedClick={onLockedClick}
            />
          ))}
        </nav>
        <SheetFooter className="px-5 pt-6">
          {hydrated && !isStandaloneDisplay() && (
            <SheetClose
              nativeButton={false}
              render={<Link to="/install" />}
              className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm"
            >
              <SmartphoneIcon className="text-primary size-4" />
              {m.nav_get_app()}
            </SheetClose>
          )}
          <a
            href={SOCIAL_LINKS.discordInvite}
            target="_blank"
            rel="noreferrer"
            className="text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm"
          >
            <BrandGlyph icon={siDiscord} fallback={ExternalLinkIcon} />
            {m.layout_header_join_discord()}
          </a>
          <p className="text-muted-foreground text-xs">{m.layout_header_tagline()}</p>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const POPOVER_ROW_CLASS = "hover:bg-muted flex items-center gap-3 rounded-md px-2 py-2 text-sm";

function PopoverRowText({ label, description }: { label: string; description: string }) {
  return (
    <div>
      <div className="font-medium">{label}</div>
      <div className="text-muted-foreground text-xs">{description}</div>
    </div>
  );
}

function InternalPopoverRow({
  to,
  icon,
  label,
  description,
}: {
  to: LinkProps["to"];
  icon: ReactNode;
  label: string;
  description: string;
}) {
  return (
    <PopoverClose nativeButton={false} render={<Link to={to} />} className={POPOVER_ROW_CLASS}>
      {icon}
      <PopoverRowText label={label} description={description} />
    </PopoverClose>
  );
}

function ExternalPopoverRow({
  href,
  icon,
  label,
  description,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  description: string;
}) {
  return (
    <PopoverClose
      nativeButton={false}
      render={
        // oxlint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label -- content is provided as children of PopoverClose
        <a href={href} target="_blank" rel="noreferrer" />
      }
      className={POPOVER_ROW_CLASS}
    >
      {icon}
      <PopoverRowText label={label} description={description} />
      <ExternalLinkIcon className="text-muted-foreground ml-auto size-3.5" />
    </PopoverClose>
  );
}

function HelpPopover() {
  const hydrated = useHydrated();
  const dismissedDate = useMilestoneBannerStore((state) => state.dismissedDate);
  const hasNewMilestone =
    hydrated && milestoneBannerDecision(latestMilestone, dismissedDate) === "show";
  return (
    <Popover>
      <PopoverTrigger render={<Button variant="ghost" size="sm" />} className="gap-1.5">
        <CircleHelpIcon className="size-4" />
        <span className="sr-only lg:not-sr-only">{m.layout_header_help()}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 gap-1 p-1.5">
        <p className="text-muted-foreground px-2 pt-1.5 pb-1 text-xs">
          {m.layout_header_help_intro()}
        </p>
        <InternalPopoverRow
          to="/help"
          icon={<CircleHelpIcon className="size-4 shrink-0" />}
          label={m.layout_header_help_articles()}
          description={m.layout_header_help_articles_description()}
        />
        <InternalPopoverRow
          to="/changelog"
          icon={
            <span className="relative shrink-0">
              <SparklesIcon className="size-4" />
              {hasNewMilestone && (
                <span
                  aria-hidden="true"
                  className="bg-primary absolute -top-0.5 -right-0.5 size-2 rounded-full"
                />
              )}
            </span>
          }
          label={m.layout_header_whats_new()}
          description={m.layout_header_whats_new_description()}
        />
        {hydrated && !isStandaloneDisplay() && (
          <InternalPopoverRow
            to="/install"
            icon={<SmartphoneIcon className="size-4 shrink-0" />}
            label={m.nav_get_app()}
            description={m.nav_get_app_description()}
          />
        )}
        <ExternalPopoverRow
          href={SOCIAL_LINKS.githubNewIssue}
          icon={<BrandGlyph icon={siGithub} fallback={ExternalLinkIcon} />}
          label={m.layout_header_report_bug()}
          description={m.layout_header_report_bug_description()}
        />
        <ExternalPopoverRow
          href={SOCIAL_LINKS.discordInvite}
          icon={<BrandGlyph icon={siDiscord} fallback={ExternalLinkIcon} />}
          label={m.layout_header_ask_discord()}
          description={m.layout_header_ask_discord_description()}
        />
      </PopoverContent>
    </Popover>
  );
}

function ContributePopover({
  isLoggedIn,
  onLockedClick,
}: {
  isLoggedIn: boolean;
  onLockedClick: (key: LockedFeatureKey) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger render={<Button variant="ghost" size="sm" />} className="gap-1.5">
        <PencilLineIcon className="size-4" />
        <span className="sr-only lg:not-sr-only">{m.layout_header_contribute()}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 gap-1 p-1.5">
        <p className="text-muted-foreground px-2 pt-1.5 pb-1 text-xs">
          {m.layout_header_contribute_intro()}
        </p>
        {isLoggedIn ? (
          <InternalPopoverRow
            to="/contribute"
            icon={<PencilLineIcon className="size-4 shrink-0" />}
            label={m.layout_header_card_data()}
            description={m.layout_header_card_data_description()}
          />
        ) : (
          <PopoverClose
            // A native <button> shrinks to its content and centers its text; force
            // it to fill and left-align so it matches the <Link>-rendered rows.
            className={cn(POPOVER_ROW_CLASS, "w-full text-left")}
            // oxlint-disable-next-line jsx-a11y/control-has-associated-label, react/forbid-elements -- bare render slot; PopoverClose owns all styling and provides the label as children
            render={<button type="button" onClick={() => onLockedClick("contribute")} />}
          >
            <PencilLineIcon className="size-4 shrink-0" />
            <PopoverRowText
              label={m.layout_header_card_data()}
              description={m.layout_header_card_data_description()}
            />
            <LockIcon className="text-muted-foreground ml-auto size-3.5 self-center" />
          </PopoverClose>
        )}
        <InternalPopoverRow
          to="/support"
          icon={<HeartIcon className="size-4 shrink-0" />}
          label={m.layout_header_support()}
          description={m.layout_header_support_description()}
        />
      </PopoverContent>
    </Popover>
  );
}

// Do not swap in a platform-detected glyph (Cmd/Ctrl): navigator-based
// detection differs between server and client and breaks SSR hydration.
function HeaderSearchButton() {
  const openPalette = useCommandPaletteStore((state) => state.openPalette);
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={m.layout_header_search()}
      onClick={openPalette}
      className="text-muted-foreground gap-1.5"
    >
      <SearchIcon className="size-4" />
      <span className="hidden lg:inline">{m.layout_header_search()}</span>
      <Kbd className="hidden lg:inline-flex">Ctrl K</Kbd>
    </Button>
  );
}

export function Header() {
  const { data: session, isPending } = useSession();
  const glossaryEnabled = useFeatureEnabled("glossary");
  const boardStatesEnabled = useFeatureEnabled("board-states");
  const scannerEnabled = useFeatureEnabled("scanner");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState<LockedFeatureKey | null>(null);
  const navFlags: NavFlags = {
    glossary: glossaryEnabled,
    "board-states": boardStatesEnabled,
    scanner: scannerEnabled,
  };
  const isLoggedIn = Boolean(session?.user);
  const { data: badges } = useBadges();
  const loansBadge = badges?.loans.total ?? 0;
  const groupsBadge = badges?.groupRequests.count ?? 0;
  const tradesBadge = badges?.trades.people ?? 0;

  return (
    <header
      data-app-header
      className={cn(
        STICKY_SURFACE,
        "border-border-accent sticky top-0 z-50 border-b pt-[env(safe-area-inset-top)]",
      )}
    >
      <div className={cn(CONTAINER_WIDTH, "px-safe grid h-14 grid-cols-[1fr_auto] items-center")}>
        {/* Left: Hamburger and logo on mobile. -ml-1 pulls the 20px glyph out
            of the 28px icon button's padding so it lands on the px-safe gutter,
            flush with page content below (cards, titles). */}
        <div className="-ml-1 flex items-center gap-1 md:hidden">
          <MenuButton onClick={() => setMobileMenuOpen(true)} />
          <LogoLink />
        </div>

        <div className="hidden gap-4 md:flex">
          <LogoLink />
          <DesktopNav
            isLoggedIn={isLoggedIn}
            flags={navFlags}
            badges={{ groups: groupsBadge, trades: tradesBadge, loans: loansBadge }}
            onLockedClick={setLockedFeature}
          />
        </div>

        {/* Right: Search + Help + Contribute + User menu. -mr-0.5 pulls the
            trailing avatar (24px in a 28px icon button) out to the px-safe
            gutter so the header's right edge aligns with flush page content. */}
        <div className="-mr-0.5 flex items-center gap-1 justify-self-end">
          <HeaderSearchButton />
          <HelpPopover />
          <ContributePopover isLoggedIn={isLoggedIn} onLockedClick={setLockedFeature} />
          <UserMenu session={session} isPending={isPending} />
        </div>
      </div>

      <MobileNav
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        isLoggedIn={isLoggedIn}
        flags={navFlags}
        badges={{ groups: groupsBadge, trades: tradesBadge, loans: loansBadge }}
        onLockedClick={setLockedFeature}
      />

      <SignInRequiredDialog
        featureKey={lockedFeature}
        onOpenChange={(dialogOpen) => {
          if (!dialogOpen) {
            setLockedFeature(null);
          }
        }}
      />
    </header>
  );
}
